import React from 'react'

import { NumberFormatValues } from 'react-number-format'

import { InfoField, InfoFieldProps } from '@/components/info-field'
import { AdornmentPosition, FormControlType } from '@/models/common'
import i18n from '@/translations'

import AsyncSelectField, { AsyncSelectProps } from './fields/async-select-field'
import CheckboxField, { CheckboxProps } from './fields/checkbox-field'
import CommentField, { CommentProps } from './fields/comment-field'
import CurrencyInputField, { CurrencyInputProps } from './fields/currency-input-field'
import DatePickerField, { DatePickerProps } from './fields/date-picker-field'
import DateRangePickerField, { DateRangePickerProps } from './fields/date-range-picker-field'
import DropzoneField, { DropzoneProps } from './fields/dropzone-field'
import FieldArray, { FieldArrayProps } from './fields/field-array'
import MonthPickerField, { MonthPickerProps } from './fields/month-picker-field'
import MultiCreatableField, { MultiCreatableFieldProps } from './fields/multi-creatable-field'
import MultiSelectField, { MultiSelectFieldProps } from './fields/multi-select-field'
import NumericInputField, { NumericInputProps } from './fields/numeric-input-field'
import PatternInputField, { PatternInputProps } from './fields/pattern-input-field'
import PhoneInputField, { PhoneInputProps } from './fields/phone-input-field'
import PublicTextField, { PublicTextProps } from './fields/public-text-field'
import RadioGroupField, { RadioGroupProps } from './fields/radio-group-field'
import RichTextField, { RichTextProps } from './fields/rich-text-field'
import SelectField, { SelectFieldProps } from './fields/select-field'
import TextField, { TextProps } from './fields/text-field'
import TextAreaField, { TextareaProps } from './fields/textarea-field'

type FormControlPropsMap = {
  [FormControlType.DISPLAY]: InfoFieldProps
  [FormControlType.TEXT]: TextProps
  [FormControlType.PUBLIC_TEXT]: PublicTextProps
  [FormControlType.TEXTAREA]: TextareaProps
  [FormControlType.COMMENT]: CommentProps
  [FormControlType.NUMERIC]: NumericInputProps
  [FormControlType.DAYS]: NumericInputProps
  [FormControlType.MONTHS]: NumericInputProps
  [FormControlType.PHONE]: PhoneInputProps
  [FormControlType.CURRENCY]: CurrencyInputProps
  [FormControlType.PERCENTAGE]: NumericInputProps
  [FormControlType.SELECT]: SelectFieldProps
  [FormControlType.ASYNC_SELECT]: AsyncSelectProps
  [FormControlType.MULTI_SELECT]: MultiSelectFieldProps
  [FormControlType.RADIO]: RadioGroupProps
  [FormControlType.CHECKBOX]: CheckboxProps
  [FormControlType.DROPZONE]: DropzoneProps
  [FormControlType.DATE_PICKER]: DatePickerProps
  [FormControlType.DATE_RANGE_PICKER]: DateRangePickerProps
  [FormControlType.MONTH_PICKER]: MonthPickerProps
  [FormControlType.RICH_TEXT]: RichTextProps
  [FormControlType.MULTI_CREATABLE]: MultiCreatableFieldProps
  [FormControlType.ARRAY]: FieldArrayProps
  [FormControlType.DUNS]: InfoFieldProps
  [FormControlType.PATTERN]: PatternInputProps
}

type FormikControlProps<T extends keyof FormControlPropsMap> = {
  control: T
} & FormControlPropsMap[T]
type ComponentMap = {
  [K in FormControlType]: React.ComponentType<FormControlPropsMap[K]>
}

const controlComponentMap: ComponentMap = {
  [FormControlType.DISPLAY]: (props: InfoFieldProps) => <InfoField {...props} />,
  [FormControlType.TEXT]: (props: TextProps) => <TextField {...props} />,
  [FormControlType.PUBLIC_TEXT]: (props: PublicTextProps) => <PublicTextField {...props} />,
  [FormControlType.TEXTAREA]: (props: TextareaProps) => <TextAreaField {...props} />,
  [FormControlType.COMMENT]: (props: CommentProps) => <CommentField {...props} />,
  [FormControlType.NUMERIC]: (props: NumericInputProps) => <NumericInputField {...props} />,
  [FormControlType.DAYS]: (props: NumericInputProps) => (
    <NumericInputField decimalScale={0} adornment={i18n.t('global.days_suffix')} adornmentPosition={AdornmentPosition.END} {...props} />
  ),
  [FormControlType.MONTHS]: (props: NumericInputProps) => (
    <NumericInputField decimalScale={0} adornment={i18n.t('global.months_suffix')} adornmentPosition={AdornmentPosition.END} {...props} />
  ),
  [FormControlType.PHONE]: (props: PhoneInputProps) => <PhoneInputField {...props} />,
  [FormControlType.CURRENCY]: (props: NumericInputProps) => <CurrencyInputField {...props} />,
  [FormControlType.PERCENTAGE]: (props: NumericInputProps) => (
    <NumericInputField
      placeholder="100%"
      suffix=" %"
      decimalSeparator="."
      decimalScale={2}
      allowNegative={false}
      isAllowed={({ floatValue }: NumberFormatValues) => floatValue == null || (floatValue >= 0 && floatValue <= 100)}
      {...props}
    />
  ),
  [FormControlType.SELECT]: (props: SelectFieldProps) => <SelectField {...props} />,
  [FormControlType.MULTI_SELECT]: (props: MultiSelectFieldProps) => <MultiSelectField {...props} />,
  [FormControlType.ASYNC_SELECT]: (props: AsyncSelectProps) => <AsyncSelectField {...props} />,
  [FormControlType.RADIO]: (props: RadioGroupProps) => <RadioGroupField {...props} />,
  [FormControlType.CHECKBOX]: (props: CheckboxProps) => <CheckboxField {...props} />,
  [FormControlType.DROPZONE]: (props: DropzoneProps) => <DropzoneField {...props} />,
  [FormControlType.DATE_PICKER]: (props: DatePickerProps) => <DatePickerField {...props} />,
  [FormControlType.DATE_RANGE_PICKER]: (props: DateRangePickerProps) => <DateRangePickerField {...props} />,
  [FormControlType.MONTH_PICKER]: (props: MonthPickerProps) => <MonthPickerField {...props} />,
  [FormControlType.RICH_TEXT]: (props: RichTextProps) => <RichTextField {...props} />,
  [FormControlType.MULTI_CREATABLE]: (props: MultiCreatableFieldProps) => <MultiCreatableField {...props} />,
  [FormControlType.ARRAY]: (props: FieldArrayProps) => <FieldArray {...props} />,
  [FormControlType.DUNS]: (props: InfoFieldProps) => <InfoField {...props} />,
  [FormControlType.PATTERN]: (props: PatternInputProps) => <PatternInputField {...props} />,
}

const FormikControl = <T extends keyof FormControlPropsMap>({ control, ...rest }: FormikControlProps<T>) => {
  const Component = controlComponentMap[control] as React.ComponentType<FormControlPropsMap[T]>

  if (Component) {
    return <Component {...rest} />
  }

  return null
}

export default FormikControl
